import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { academicYearActions } from "../slices/academicYear";
import { getErrorMessage } from "../../utils/errorUtils";
import { AcademicYearRequestDto } from "../../shared/dtos/academicYear/AcademicYearRequestDto";
import { AcademicYearResponseDto } from "../../shared/dtos/academicYear/AcademicYearResponseDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const createAcademicYear =
  (values: AcademicYearRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(academicYearActions.setLoadingCreate());

    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<AcademicYearResponseDto>
      >("/academic-years", values, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Academic year created successfully!");

        dispatch(academicYearActions.createSuccess(data.data));
        dispatch(
          academicYearActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(academicYearActions.setErrorCreate(message));
    }
  };

export const getAcademicYears = () => async (dispatch: AppDispatch) => {
  dispatch(academicYearActions.setLoadingFetchAll());

  try {
    const { data } = await axiosInstance.get<
      ApiResponseDto<AcademicYearResponseDto[]>
    >("/academic-years", { headers: { requiresAuth: true } });

    if (data.status === ApiResponseStatus.SUCCESS && data.data) {
      dispatch(academicYearActions.setItems(data.data));
    }
  } catch (error) {
    dispatch(academicYearActions.setErrorFetchAll(getErrorMessage(error)));
  }
};

export const getAcademicYearById =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(academicYearActions.setLoadingFetchOne());

    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<AcademicYearResponseDto>
      >(`/academic-years/${id}`, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(academicYearActions.setItem(data.data));
      }
    } catch (error) {
      dispatch(academicYearActions.setErrorFetchOne(getErrorMessage(error)));
    }
  };

export const updateAcademicYear =
  (payload: { id: string; values: AcademicYearRequestDto }) =>
  async (dispatch: AppDispatch) => {
    dispatch(academicYearActions.setLoadingUpdate());

    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<AcademicYearResponseDto>
      >(`/academic-years/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Academic year updated successfully!");

        dispatch(academicYearActions.updateSuccess(data.data));
        dispatch(
          academicYearActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(academicYearActions.setErrorUpdate(message));
    }
  };

export const deleteAcademicYear =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(academicYearActions.setLoadingDelete());

    try {
      const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
        `/academic-years/${id}`,
        { headers: { requiresAuth: true } },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Academic year deleted successfully!");

        dispatch(academicYearActions.deleteSuccess(id));
        dispatch(
          academicYearActions.setServerStatus({
            operation: "delete",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(academicYearActions.setErrorDelete(message));
    }
  };

export const resetAcademicYearServerStatus = () => (dispatch: AppDispatch) => {
  dispatch(academicYearActions.setServerStatus(null));
};
